/**
 * Rx-01 — pharmacist order queue: Mine (Ongoing / Completed) / Unassigned / All.
 *
 * Search is instant + server (lib/queueSearch.ts): typing filters the loaded rows at once, the
 * server search runs after a short pause and its answer replaces them. Mine → Completed holds the
 * finished orders (delivered, cancelled, …) page by page, filterable by status and order date.
 */

import {
  infiniteQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { listCatalogueStatuses } from "../../api/catalogue";
import { listQueue } from "../../api/orders";
import type { PaginatedQueue, QueueOrder } from "../../api/orders";
import { OrderStatusBadge } from "../../components/OrderStatusBadge";
import { ExportButton } from "../../components/ui/ExportButton";
import { Select } from "../../components/ui/Select";
import { uniqueRows } from "../../lib/newOrders";
import { dateRange, matchesSearch, refreshCachedRows } from "../../lib/queueSearch";
import {
  byPageNumber,
  EXPORT_PAGE_SIZE,
  sheetDateTime,
  sheetPhone,
  type ExportColumn,
} from "../../lib/tableExport";
import { useDebouncedValue } from "../../lib/useDebouncedValue";

const TABS = [
  { key: "mine", label: "Mine" },
  { key: "unassigned", label: "Unassigned" },
  { key: "all", label: "All" },
] as const;
type Tab = (typeof TABS)[number]["key"];
type MineView = "ongoing" | "completed";

const PAGE = 25;
const COMPLETED_PAGE = 20;

// An export is every order the tab (and its search / filters) holds, not only the loaded pages.
const EXPORT_COLUMNS: ExportColumn<QueueOrder>[] = [
  { header: "Order", value: (o) => o.order_id },
  { header: "Placed (IST)", value: (o) => sheetDateTime(o.created_at) },
  { header: "Patient", value: (o) => o.patient_name },
  { header: "Phone", value: (o) => sheetPhone(o.patient_phone) },
  { header: "For a family member", value: (o) => o.for_family_member_id !== null },
  { header: "Status", value: (o) => o.status_label },
  { header: "Amount (₹)", value: (o) => o.final_amount },
  { header: "Priced (IST)", value: (o) => sheetDateTime(o.pricing_published_at) },
  { header: "Items", value: (o) => o.line_count },
];

export function Queue() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("unassigned");
  const [mineView, setMineView] = useState<MineView>("ongoing");
  const [q, setQ] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const term = q.trim();

  // Reloads this tab's lists (incl. an active search) from the server, not the whole page.
  const refresh = async () => {
    setRefreshing(true);
    try {
      await qc.refetchQueries({ queryKey: ["queue", tab], type: "active" });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Order queue</h2>
        <p className="text-sm text-gray-500">Claim, price and publish patient orders.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-lg px-3 py-1.5 ${tab === t.key ? "bg-brand text-brand-fg" : "border"}`}
          >
            {t.label}
          </button>
        ))}
        <button
          onClick={() => void refresh()}
          disabled={refreshing}
          className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-gray-700 hover:border-brand hover:text-brand disabled:opacity-50"
        >
          <RefreshIcon spinning={refreshing} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search order no, name or phone…"
          aria-label="Search by order number, patient name or phone"
          // Full width on its own line on a phone, not a narrow box pushed to the right (RESP-23).
          className="w-full rounded-lg border px-3 py-1.5 outline-none focus:border-brand focus:ring-1 focus:ring-brand sm:ml-auto sm:w-64"
        />
      </div>

      {tab === "mine" && (
        <div className="inline-flex rounded-lg border bg-white p-0.5 text-sm" role="tablist" aria-label="My orders">
          {(["ongoing", "completed"] as const).map((view) => (
            <button
              key={view}
              role="tab"
              aria-selected={mineView === view}
              onClick={() => setMineView(view)}
              className={`rounded-md px-3 py-1 capitalize ${mineView === view ? "bg-brand-50 font-medium text-brand" : "text-ink-soft"}`}
            >
              {view}
            </button>
          ))}
        </div>
      )}

      {tab === "mine" && mineView === "completed" ? (
        <CompletedOrders term={term} />
      ) : (
        <LiveOrders key={`${tab}-${mineView}`} tab={tab} scope={tab === "mine" ? "ongoing" : undefined} term={term} />
      )}
    </div>
  );
}

/** Unassigned / All / Mine → Ongoing: newest first, "Load more" at the end. */
function LiveOrders({ tab, scope, term }: { tab: Tab; scope?: "ongoing"; term: string }) {
  const qc = useQueryClient();
  const serverTerm = useDebouncedValue(term, 300);
  const options = (q: string) =>
    infiniteQueryOptions({
      queryKey: ["queue", tab, q, scope ?? ""],
      queryFn: ({ pageParam }) => listQueue(tab, pageParam, PAGE, q, scope ? { scope } : {}),
      initialPageParam: 1,
      getNextPageParam: (last: PaginatedQueue) => (last.has_more ? last.page + 1 : undefined),
    });
  const all = useInfiniteQuery(options(""));
  const found = useInfiniteQuery({ ...options(serverTerm), enabled: serverTerm !== "" });

  // The server's rows are fresher: write them over cached copies (no stale duplicates later).
  useEffect(() => {
    if (found.data) refreshCachedRows(qc, uniqueRows(found.data));
  }, [found.data, qc]);

  const answered = term !== "" && serverTerm === term && found.isSuccess;
  const active = term === "" ? all : answered ? found : null; // whose pages "Load more" extends
  const rows = term === "" ? uniqueRows(all.data) : answered ? uniqueRows(found.data) : uniqueRows(all.data).filter((o) => matchesSearch(o, term));

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <ExportButton
          table="orders"
          columns={EXPORT_COLUMNS}
          keyOf={(o) => o.id}
          fetchPage={byPageNumber((page) => listQueue(tab, page, EXPORT_PAGE_SIZE, term, scope ? { scope } : {}))}
        />
      </div>
      {term !== "" && !answered && (
        <p className="text-xs text-ink-muted" aria-live="polite">
          Showing matches already on screen · searching all orders…
        </p>
      )}
      {all.isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      {rows.map((o) => (
        <OrderCard key={o.id} order={o} />
      ))}
      {!all.isLoading && rows.length === 0 && (term === "" || answered) && (
        <p className="text-sm text-gray-400">{term ? `No orders matching “${term}”.` : "No orders in this tab."}</p>
      )}
      {active?.hasNextPage && (
        <button onClick={() => void active.fetchNextPage()} disabled={active.isFetchingNextPage} className="text-sm text-brand hover:underline">
          {active.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

/** Mine → Completed: finished orders, page by page, filtered by status and order date. */
function CompletedOrders({ term }: { term: string }) {
  const qc = useQueryClient();
  const serverTerm = useDebouncedValue(term, 300);
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // Any filter change goes back to page 1 (derived, so no reset effect is needed).
  const filterKey = [serverTerm, status, from, to].join("|");
  const [paging, setPaging] = useState({ key: filterKey, page: 1 });
  const page = paging.key === filterKey ? paging.page : 1;
  const goTo = (p: number) => setPaging({ key: filterKey, page: p });

  const statuses = useQuery({ queryKey: ["catalogue-statuses"], queryFn: listCatalogueStatuses, staleTime: 5 * 60_000 });
  const finalStatuses = (statuses.data ?? []).filter((s) => s.is_final && s.active).sort((a, b) => a.display_order - b.display_order);

  const range = dateRange(from, to);
  const options = (q: string) => ({
    queryKey: ["queue", "mine", q, "completed", status, from, to, page],
    queryFn: () => listQueue("mine", page, COMPLETED_PAGE, q, { scope: "completed" as const, status, ...range }),
    placeholderData: keepPreviousData,
    enabled: range !== null,
  });
  const listed = useQuery(options(""));
  const found = useQuery({ ...options(serverTerm), enabled: range !== null && serverTerm !== "" });

  useEffect(() => {
    if (found.data) refreshCachedRows(qc, found.data.data);
  }, [found.data, qc]);

  const answered = term !== "" && serverTerm === term && found.isSuccess && !found.isPlaceholderData;
  const shown = term === "" ? listed.data : answered ? found.data : undefined;
  const rows: QueueOrder[] = shown ? shown.data : (listed.data?.data ?? []).filter((o) => matchesSearch(o, term));
  const filtered = status !== "" || from !== "" || to !== "";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-white p-3 text-sm">
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          Status
          <Select
            value={status}
            options={[{ value: "", label: "All completed" }, ...finalStatuses.map((s) => ({ value: s.code, label: s.label }))]}
            onChange={setStatus}
            className="rounded-md pl-2 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          Ordered from
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="rounded-md border px-2 py-1 text-sm text-ink" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          Ordered to
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="rounded-md border px-2 py-1 text-sm text-ink" />
        </label>
        {filtered && (
          <button
            onClick={() => {
              setStatus("");
              setFrom("");
              setTo("");
            }}
            className="pb-1.5 text-xs font-medium text-brand hover:underline"
          >
            Clear filters
          </button>
        )}
        <span className="ml-auto self-end">
          <ExportButton
            table="completed orders"
            columns={EXPORT_COLUMNS}
            keyOf={(o) => o.id}
            disabled={range === null}
            fetchPage={byPageNumber((page) =>
              listQueue("mine", page, EXPORT_PAGE_SIZE, term, { scope: "completed" as const, status, ...range }),
            )}
          />
        </span>
      </div>
      {range === null && <p className="text-sm text-red-600">The “from” date must be on or before the “to” date.</p>}

      {term !== "" && !answered && (
        <p className="text-xs text-ink-muted" aria-live="polite">
          Showing matches on this page · searching all completed orders…
        </p>
      )}
      {listed.isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      <div className={`space-y-3 ${listed.isPlaceholderData ? "opacity-60" : ""}`}>
        {rows.map((o) => (
          <OrderCard key={o.id} order={o} />
        ))}
      </div>
      {!listed.isLoading && rows.length === 0 && range !== null && (term === "" || answered) && (
        <p className="text-sm text-gray-400">
          {term ? `No completed orders matching “${term}”.` : filtered ? "No completed orders for these filters." : "No completed orders yet."}
        </p>
      )}
      {shown && shown.total > 0 && <Pager page={shown.page} perPage={shown.per_page} total={shown.total} onPage={goTo} />}
    </div>
  );
}

function Pager({ page, perPage, total, onPage }: { page: number; perPage: number; total: number; onPage: (page: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  const first = (page - 1) * perPage + 1;
  const last = Math.min(page * perPage, total);
  return (
    <nav className="flex flex-wrap items-center justify-between gap-3 pt-1 text-sm" aria-label="Pages">
      <span className="text-xs text-ink-muted">
        Showing {first}–{last} of {total}
      </span>
      <div className="flex items-center gap-2">
        <button onClick={() => onPage(page - 1)} disabled={page <= 1} className="rounded-lg border px-3 py-1 disabled:opacity-40">
          Previous
        </button>
        <span className="text-xs text-ink-soft">
          Page {page} of {pages}
        </span>
        <button onClick={() => onPage(page + 1)} disabled={page >= pages} className="rounded-lg border px-3 py-1 disabled:opacity-40">
          Next
        </button>
      </div>
    </nav>
  );
}

function OrderCard({ order: o }: { order: QueueOrder }) {
  return (
    <Link to={`/staff/pharmacist/orders/${o.id}`} className="block rounded-xl border bg-white p-4 shadow-xs transition hover:border-brand">
      {/* Row 1: the order number first, then who ordered it. */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-base font-bold text-ink">{o.order_id}</span>
        {o.patient_name ? (
          <span className="min-w-0 truncate text-sm text-ink-soft">
            <span className="font-medium text-ink">{o.patient_name}</span>
            {o.patient_phone && <span> · {formatPhone(o.patient_phone)}</span>}
          </span>
        ) : (
          // A row just added from a push carries no patient details (they stay out of FCM).
          <span className="text-sm text-ink-muted">New order</span>
        )}
      </div>
      {/* Row 2: where it stands. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <OrderStatusBadge code={o.status_code} label={o.status_label} />
        <span className="text-xs text-ink-muted">
          {o.line_count} item{o.line_count === 1 ? "" : "s"}
        </span>
        <span className="ml-auto text-xs text-gray-400">{new Date(o.created_at).toLocaleString()}</span>
      </div>
    </Link>
  );
}

/** "+919629009376" → "+91 96290 09376" (other formats as they are). */
function formatPhone(phone: string): string {
  const m = /^\+91(\d{5})(\d{5})$/.exec(phone);
  return m ? `+91 ${m[1]} ${m[2]}` : phone;
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`h-4 w-4 ${spinning ? "animate-spin" : ""}`}
      aria-hidden
    >
      <path d="M20 12a8 8 0 1 1-2.34-5.66" />
      <path d="M20 4v4h-4" />
    </svg>
  );
}
